import { consume, ContextType } from '@lit/context';
import { css, html, LitElement, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { contexts } from '../contexts.js';
import { forgeUtils } from '../forgeUtils.js';
import { PageOrganization } from '../page-organization.js';
import { PageProjectAccess } from '../page-project-access.js';
import { PageProjectAnalytics } from '../page-project-analytics.js';
import { PageProjectHistory } from '../page-project-history.js';
import { PageProjectPulls } from '../page-project-pulls.js';
import { PageProjectRuns } from '../page-project-runs.js';
import { PageProjectSettings } from '../page-project-settings.js';
import { PageProjectSuperadm } from '../page-project-superadm.js';
import { PageProjectTests } from '../page-project-tests.js';
import { PageProjectUsage } from '../page-project-usage.js';
import { linkStyles } from './cssstyles.js';

@customElement('project-header')
export class ProjectHeader extends LitElement {
  @consume({ context: contexts.project, subscribe: true }) @state() private _project?: ContextType<typeof contexts.project>;
  @consume({ context: contexts.dataSnapshotId, subscribe: true }) @state() private _snapshotId?: ContextType<typeof contexts.dataSnapshotId>;
  @consume({ context: contexts.user, subscribe: true }) @state() private _user?: ContextType<typeof contexts.user>;
  @consume({ context: contexts.projectReferences, subscribe: true }) @state() private _projectReferences?: ContextType<typeof contexts.projectReferences>;
  @consume({ context: contexts.projectPulls, subscribe: true }) @state() private _projectPulls?: ContextType<typeof contexts.projectPulls>;
  @consume({ context: contexts.testsStatus, subscribe: true }) @state() private _testsStatus?: ContextType<typeof contexts.testsStatus>;

  @property({ attribute: false }) orgSlug?: string;
  @property({ attribute: false }) projectSlug?: string;
  @property({ attribute: false }) submenu?: string;

  override render() {
    const orgSlug = this.orgSlug;
    const projectSlug = this.projectSlug;
    const regressions = this._testsStatus?.regressed ?? 0;
    const failures = this._testsStatus?.unexpected ?? 0;
    return html`
      <org-restrictions-banner .orgSlug=${this.orgSlug}></org-restrictions-banner>
      <storage-warning-banner .orgSlug=${this.orgSlug}></storage-warning-banner>
      <subscription-trial-banner .orgSlug=${this.orgSlug}></subscription-trial-banner>
      <missing-billing-email-banner .orgSlug=${this.orgSlug}></missing-billing-email-banner>
      <app-header>
        <h-box style="gap: var(--sl-spacing-2x-small);">
        <sl-breadcrumb>
          <span slot="separator">/</span>
          ${this.orgSlug ? html`
            <sl-breadcrumb-item href=${this.orgSlug ? PageOrganization.url({ orgSlug: this.orgSlug }) : nothing }>${this.orgSlug ?? nothing}</sl-breadcrumb-item>
          ` : nothing}
          ${orgSlug && projectSlug ? html`
            <sl-breadcrumb-item href=${orgSlug && projectSlug && PageProjectTests.url({ orgSlug, projectSlug, snapshotId: this._snapshotId })}>
              <h-box style="gap: var(--sl-spacing-2x-small);"><span><b>${projectSlug}</b></span><project-visibility value=${this._project?.visibility}></project-visibility></h-box>
            </sl-breadcrumb-item>
          ` : nothing}
        </sl-breadcrumb>

        ${forgeUtils.hasSourceUrl(this._project) ? html`
          <sl-icon name=dash-lg></sl-icon>
          <a-ext href=${forgeUtils.projectUrl(this._project)} class=project-link>${forgeUtils.projectUrl(this._project).replace(/^https:\/\//, '')}</a-ext>
        ` : nothing}
        </h-box>

        ${this.orgSlug && this.projectSlug ? html`
          <app-header-submenu
            href=${PageProjectTests.url({ orgSlug: this.orgSlug, projectSlug: this.projectSlug, snapshotId: this._snapshotId })}
            ?active=${this.submenu === 'tests'}
          ><sl-icon name=ui-checks></sl-icon>Tests
            ${regressions > 0 ? html`
              <sl-tag variant=danger size=small pill>${regressions} regression${regressions > 1 ? 's' : ''}</sl-tag>
            ` : failures > 0 ? html`
              <sl-tag variant=danger size=small pill>${failures} failure${failures > 1 ? 's' : ''}</sl-tag>
            ` : nothing}

          </app-header-submenu>
          
          ${this._project?.sourceType === 'local_checkout' ? nothing : html`
            <app-header-submenu
              href=${PageProjectPulls.url({ orgSlug: this.orgSlug, projectSlug: this.projectSlug, snapshotId: this._snapshotId })}
              ?active=${this.submenu === 'pulls'}
            ><sl-icon name=bx-git-pull-request library=boxicons></sl-icon>${forgeUtils.pullRequestLabelPlural(this._project)}${
              this._projectPulls === undefined
                ? html`<sl-skeleton effect=pulse style="width: 2ch"></sl-skeleton>`
                : html`<sl-tag size=small pill>${this._projectPulls.totalElements}</sl-tag>`
            }</app-header-submenu>
          `}
          <app-header-submenu
            href=${PageProjectHistory.url({ orgSlug: this.orgSlug, projectSlug: this.projectSlug, snapshotId: this._snapshotId })}
            ?active=${this.submenu === 'history'}
          ><sl-icon name=bx-git-branch library=boxicons></sl-icon>History</app-header-submenu>
          <app-header-submenu
            href=${PageProjectAnalytics.url({ orgSlug: this.orgSlug, projectSlug: this.projectSlug, snapshotId: this._snapshotId })}
            ?active=${this.submenu === 'analytics'}
          ><sl-icon name=bar-chart-line></sl-icon>Analytics</app-header-submenu>
          <app-header-submenu
            href=${PageProjectRuns.url({ orgSlug: this.orgSlug, projectSlug: this.projectSlug, snapshotId: this._snapshotId })}
            ?active=${this.submenu === 'runs'}
          >
            <sl-icon name=play-circle></sl-icon>Runs
            ${(this._project?.snapshot?.malformedRunsCount ?? 0) > 0 ? html`
              <sl-icon style="color: var(--sl-color-warning-500);" name=exclamation-circle-fill></sl-icon>
            ` : nothing}
          </app-header-submenu>
          <app-header-submenu
            href=${PageProjectAccess.url({ orgSlug: this.orgSlug, projectSlug: this.projectSlug, snapshotId: this._snapshotId })}
            ?active=${this.submenu === 'access'}
          ><sl-icon name=people></sl-icon>Access</app-header-submenu>
          <app-header-submenu
            href=${PageProjectUsage.url({ orgSlug: this.orgSlug, projectSlug: this.projectSlug, snapshotId: this._snapshotId })}
            ?active=${this.submenu === 'usage'}
          ><sl-icon name=speedometer2></sl-icon>Usage</app-header-submenu>
          <app-header-submenu
            href=${PageProjectSettings.url({ orgSlug: this.orgSlug, projectSlug: this.projectSlug, snapshotId: this._snapshotId })}
            ?active=${this.submenu === 'settings'}
          ><sl-icon name=gear></sl-icon>Settings</app-header-submenu>
          ${this._user?.isSuperUser ? html`
            <app-header-submenu
              href=${PageProjectSuperadm.url({ orgSlug: this.orgSlug, projectSlug: this.projectSlug, snapshotId: this._snapshotId })}
              ?active=${this.submenu === 'superadm'}
            ><sl-icon name=tools></sl-icon>SuperAdm</app-header-submenu>
          ` : nothing}
          <x-filler slot=second-line></x-filler>
          <snapshot-refresh-button slot=second-line></snapshot-refresh-button>
          <timezone-selector subtle slot=second-line ?disabled=${this._project?.access !== 'editor'}></timezone-selector>
        ` : nothing}
      </app-header>
    `;
  }

  static styles = [linkStyles, css`
    :host {
      display: block;
    }

    project-visibility {
      font-size: var(--sl-font-size-small);
    }

    sl-breadcrumb-item::part(base) {
      font-size: var(--sl-font-size-medium);
      font-weight: var(--sl-font-weight-normal);
    }

    sl-breadcrumb-item::part(label) {
      color: var(--sl-color-neutral-800);
    }

    sl-tag::part(base) {
      height: 16px;
    }

    .project-link {
      color: var(--sl-color-neutral-400);

      &:hover {
        color: unset;
      }
    }
  `];
}
